import '../index.less';

import { Table, message } from 'antd';

import { numberWithCommas } from '../../../helpers/number';
import { displayDateAndTime } from '../../../utils/datetime.util';

import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { specialTaggingDescription } from '../../../utils/specialTaggingDesc.util';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store/store';
import { getTaggingHistoryByAccountDetailsIdThunk } from '../../../services/collectionService/collectionThunk';
import { AccountEnquiryResponse } from '../../../features/collection/collectionSlice';

type SpecialTaggingHistoryProps = {
  accountEnquiry: AccountEnquiryResponse;
};

const SpecialTaggingHistory: React.FC<SpecialTaggingHistoryProps> = (props) => {
  const { accountEnquiry } = props;
  const dispatch = useDispatch<AppDispatch>();

  const [taggingHistoryData, setTaggingHistoryData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: 1,
        pageSize: 10,
      },
      sortOrder: 'desc',
      sortField: 'createdAt',
      search: {},
      filters: {},
    }),
    []
  );

  const [paginationState, setPaginationState] = useState(initialParams.pagination);

  const tablePagination = {
    total: taggingHistoryData?.total,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    currentPage: paginationState.current,
    pageSize: paginationState.pageSize,
  };

  const columns: ColumnsType<any> = [
    {
      title: 'No.',
      key: 'id',
      render: (_, __, index: number) => index + 1,
    },
    {
      title: 'Date & Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text: string, record: any) => (text ? displayDateAndTime(text) : '-'),
    },
    {
      title: 'Special Tagging Code',
      dataIndex: 'taggingName',
      key: 'taggingName',
    },
    {
      title: 'Special Tagging Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'History / Remarks',
      dataIndex: 'historyRemarks',
      key: 'historyRemarks',
      render: (text: string, record: any) => (text ? text : '-'),
    },
    {
      title: 'Documents',
      dataIndex: 'documents',
      key: 'documents',
      render: (text: string, record: any) => (text ? text : '-'),
    },
  ];

  const fetchTaggingHistory = async (params?: any) => {
    setLoading(true);
    await dispatch(getTaggingHistoryByAccountDetailsIdThunk({ ...params }))
      .unwrap()
      .then((res) => {
        setTaggingHistoryData(res);
      })
      .catch((err) => {
        message.error('Failed to fetch special tagging history');
        console.error('Failed to fetch special tagging history', err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTableChange = async (pagination: TablePaginationConfig) => {
    fetchTaggingHistory({
      ...initialParams,
      pagination,
      accountDetailsId: accountEnquiry?.accountDetails?.id,
    });
    setPaginationState({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
    });
  };

  useEffect(() => {
    if (accountEnquiry?.accountDetails?.id) {
      fetchTaggingHistory({
        ...initialParams,
        accountDetailsId: accountEnquiry?.accountDetails?.id,
      });
    }
  }, [accountEnquiry?.accountDetails?.id]);

  return (
    <div className="account-enquiry-container">
      <div className="account-enquiry-section-container">
        <div className="info-container">
          <Table
            columns={columns}
            dataSource={taggingHistoryData?.data}
            pagination={tablePagination}
            loading={loading}
            onChange={handleTableChange}
          />
        </div>
      </div>
    </div>
  );
};

export default SpecialTaggingHistory;
